import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ErrorState, LoadingState } from '@/components/primitives';
import OrgForm from '@/components/org/OrgForm';
import { emptyOrg, fieldErrors, fromProfile, toPayload } from '@/components/org/orgModel';
import { adminApi, toastError, useAdminQuery } from './adminApi';

/**
 * Create or edit an organization with its detailed profile.
 *   userId == null  -> create: also asks for the owner's email, then returns the invite link
 *   userId set      -> edit the existing organization
 */
export default function OrganizationDialog({ open, userId, onOpenChange, onSaved }) {
  const editing = Boolean(userId);
  const options = useAdminQuery(() => adminApi.get('/admin/organizations/options').then((r) => r.data), [], { enabled: open });
  const existing = useAdminQuery(
    () => adminApi.get(`/admin/organizations/${userId}`).then((r) => r.data),
    [userId],
    { enabled: open && editing },
  );
  const [form, setForm] = useState(emptyOrg);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(editing ? (existing.data ? fromProfile(existing.data) : emptyOrg()) : emptyOrg());
  }, [open, editing, existing.data]);

  const loading = options.loading || (editing && existing.loading);
  const loadError = options.error || (editing && existing.error);
  const errorCount = Object.keys(errors).length;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      const body = toPayload(form);
      let res;
      if (editing) {
        res = await adminApi.put(`/admin/organizations/${userId}`, body);
        toast.success(`${form.company_name} updated`);
      } else {
        res = await adminApi.post('/admin/organizations', { ...body, owner_email: form.owner_email.trim(), owner_name: form.owner_name.trim() });
        toast.success(`${form.company_name} created`);
      }
      onSaved(res.data, editing);
      onOpenChange(false);
    } catch (err) {
      const fe = fieldErrors(err);
      if (Object.keys(fe).length) {
        setErrors(fe);
        toast.error('Some fields need attention', { description: 'They are marked below.' });
        requestAnimationFrame(() => document.querySelector('.of-error')?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
      } else {
        toastError(err, editing ? 'Could not save the organization' : 'Could not create the organization');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="adm-dialog adm-dialog--editor" data-testid="organization-dialog">
        <DialogHeader className="adm-editor-head">
          <DialogTitle>{editing ? `Edit ${existing.data?.company_name || 'organization'}` : 'New organization'}</DialogTitle>
          <DialogDescription>
            {editing
              ? 'Changes apply immediately and re-match existing stories to the updated profile.'
              : 'Create the customer organization and its first user. You get a one-time invite link; the owner chooses their own password.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="adm-editor-form" noValidate>
          <div className="adm-editor-body">
            {loading ? <LoadingState label="the form" rows={5} />
              : loadError ? <ErrorState title="Couldn't load the form" error={loadError} onRetry={() => { options.reload(); existing.reload?.(); }} />
                : (
                  <OrgForm form={form} onChange={setForm} options={options.data || {}} errors={errors} showInternal>
                    {!editing && (
                      <div className="of-grid" data-testid="owner-fields">
                        <div className="of-field">
                          <label className="of-label" htmlFor="org-owner-email">Owner email</label>
                          <input id="org-owner-email" className="of-input" type="email" autoComplete="off" value={form.owner_email}
                                 aria-invalid={errors.owner_email ? 'true' : undefined}
                                 onChange={(e) => setForm({ ...form, owner_email: e.target.value })} required />
                          {errors.owner_email ? <span className="of-error" role="alert">{errors.owner_email}</span> : <span className="of-hint">The invite link is for this person. They become the organization's first user.</span>}
                        </div>
                        <div className="of-field">
                          <label className="of-label" htmlFor="org-owner-name">Owner name</label>
                          <input id="org-owner-name" className="of-input" value={form.owner_name} maxLength={120} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} />
                        </div>
                      </div>
                    )}
                  </OrgForm>
                )}
          </div>
          <div className="adm-editor-foot">
            {errorCount > 0 && <span className="adm-meta" role="status" style={{ marginRight: 'auto', color: 'var(--state-critical)' }}>{errorCount} field{errorCount === 1 ? '' : 's'} need attention</span>}
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving || loading || Boolean(loadError)} data-testid="organization-submit">
              {saving && <Loader2 className="is-spinner" aria-hidden="true" />}
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create and generate invite'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
